import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function DocFussion() {
  const BACKEND = useMemo(() => {
    const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
    if (env) return env;
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      // return 'http://localhost:8000';
      return 'https://mhub.maskantech.in';
    }
    return '';
  }, []);

  const PDF_URL = `${BACKEND}/api/docfussion/input-pdf`;
  const HTML_URL = `${BACKEND}/api/docfussion/output-html`;
  const VIDEO_URL = `${BACKEND}/api/docfussion/video`;

  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [htmlText, setHtmlText] = useState('');
  const [isFs, setIsFs] = useState(false);
  const compareRef = useRef(null);

  useEffect(() => {
    let revoke = '';
    fetch(PDF_URL)
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => {
        const u = URL.createObjectURL(b);
        revoke = u;
        setPdfBlobUrl(u);
      })
      .catch(() => setPdfBlobUrl(''));
    fetch(HTML_URL)
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((t) => setHtmlText(t))
      .catch(() => setHtmlText('<div style="padding:10px;color:#b02a37">Failed to load HTML</div>'));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [PDF_URL, HTML_URL]);

  useEffect(() => {
    const onFs = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const enterFullscreen = () => {
    const el = compareRef.current;
    if (el && el.requestFullscreen) {
      el.requestFullscreen().catch(() => setIsFs(true));
    } else {
      setIsFs(true);
    }
  };
  const exitFullscreen = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => setIsFs(false));
    }
    setIsFs(false);
  };

  return (
    <div className="page">
      <header className="header"><h1>DocFussion</h1></header>
      <main className="container">
        <section className="panel df-hero">
          <h2 className="title-center">AI-Powered PDF to Structured Content</h2>
          <p className="lead-center">Convert PDFs into clean Markdown, XML, and HTML with high accuracy. DocFussion combines OCR, layout analysis, table extraction, and versioning into one seamless workflow.</p>
          <p className="lead-center">From upload to export—fast, accurate, and collaborative document transformation.</p>
        </section>

        <section className="panel df-flow">
          <h3 className="flow-title">How It Works</h3>
          <div className="flow-grid">
            <div className="flow-step"><div className="ico">📤</div><div className="step-title">1) Upload</div><p>Securely upload your PDF. Large files supported. Streaming with progress.</p></div>
            <div className="flow-step"><div className="ico">🧭</div><div className="step-title">2) OCR & Layout</div><p>Multi-model pipeline extracts text, structure, and tables. 90+ languages via OCR.</p></div>
            <div className="flow-step"><div className="ico">🧠</div><div className="step-title">3) AI Conversion</div><p>Generate Markdown, XML, and HTML with reading order preserved. Tables → structured data.</p></div>
            <div className="flow-step"><div className="ico">✏️</div><div className="step-title">4) Review & Edit</div><p>In-browser editors with live preview. Fix, format, and annotate before exporting.</p></div>
            <div className="flow-step"><div className="ico">👥</div><div className="step-title">5) Share & Version</div><p>Collaborate with roles. Version history and diff included.</p></div>
            <div className="flow-step"><div className="ico">📦</div><div className="step-title">6) Export & Deliver</div><p>Export Markdown, XML, HTML,EPUB,Audiobook, JSON (and HTML preview). Download or stream.</p></div>
          </div>
          <div className="notes-band">
            <div className="notes-title">Key Advantages</div>
            <div className="notes-grid">
              <div className="note-item">Accurate by Design — six specialized AI models</div>
              <div className="note-item">Fast Pipeline — GPU ~1–2s/page</div>
              <div className="note-item">Collaboration-Ready — RBAC + version control</div>
              <div className="note-item">Developer-Friendly — FastAPI + React</div>
              <div className="note-item">Secure & Private — JWT + server-side processing</div>
            </div>
          </div>
        </section>

        <section className="panel df-compare">
          <div className="compare-head">
            <h3 className="flow-title">Input vs Output</h3>
            {isFs ? (
              <button className="fs-btn" onClick={exitFullscreen}>Exit Fullscreen</button>
            ) : (
              <button className="fs-btn" onClick={enterFullscreen}>Fullscreen</button>
            )}
          </div>
          <div className="compare-grid" ref={compareRef}>
            <div className="pane">
              <div className="pane-head">Input PDF</div>
              {pdfBlobUrl ? (
                <object data={pdfBlobUrl} type="application/pdf" style={{width:'100%', height:'100%'}}>
                  <iframe title="Input PDF" src={pdfBlobUrl} style={{width:'100%', height:'100%', border:0}} />
                </object>
              ) : (
                <div style={{padding:10, color:'#6b7a99'}}>Loading PDF…</div>
              )}
            </div>
            <div className="pane">
              <div className="pane-head">Output HTML</div>
              <iframe title="Output HTML" srcDoc={htmlText} style={{width:'100%', height:'100%', border:0}} sandbox="allow-scripts allow-same-origin" />
            </div>
          </div>
        </section>

        <section className="panel df-demo">
          <h3 className="flow-title">End-to-End Demo</h3>
          <div className="video-full">
            <video src={VIDEO_URL} controls preload="metadata" playsInline style={{ width:'100%', height:'100%', objectFit:'contain' }}>
              Your browser does not support the video tag.
            </video>
          </div>
        </section>

        <style>{`
          .df-hero { background: linear-gradient(180deg,#f7fbff,#ecf5ff); border:1px solid #e6eaf1; text-align:center; border-radius: 20px; }
          .title-center { margin: 0 0 8px; font-size: 28px; font-weight: 800; }
          .lead-center { color:#2a3553; max-width: 900px; margin: 0 auto; }

          .df-flow { margin-bottom: 20px; }
          .df-flow .flow-title { margin:0 0 12px; font-size: 20px; }
          .flow-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; }
          .flow-step { background:#fff; border:1px solid #e6eaf1; border-radius:12px; padding:14px; box-shadow:0 8px 24px rgba(2,6,23,.06); }
          .flow-step .ico { width:36px; height:36px; display:flex; align-items:center; justify-content:center; background:#eef3ff; border:1px solid #d7e1ff; border-radius:10px; margin-bottom:8px; }
          .step-title { font-weight:700; margin-bottom:4px; }
          .notes-band { margin-top:12px; background:#eaf1ff; border:1px solid #cdd8ff; border-radius:14px; padding:16px; text-align:center; }
          .notes-title { color:#2C388F; font-weight:800; font-size:18px; }
          .notes-grid { margin-top:10px; display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; color:#2C388F; font-weight:600; }
          .note-item { background:#fff; border:1px solid #e6eaf1; border-radius:10px; padding:10px; }

          .compare-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
          .fs-btn { background:#1e2a55; color:#fff; border:1px solid #1e2a55; border-radius:10px; padding:8px 12px; cursor:pointer; }
          .compare-grid { display:grid; grid-template-columns: 1fr 1fr; gap:12px; height:70vh; }
          .pane { background:#fff; border:1px solid #e6eaf1; border-radius:12px; box-shadow:0 8px 24px rgba(2,6,23,.06); display:flex; flex-direction:column; }
          .pane-head { padding:10px 12px; border-bottom:1px solid #e6eaf1; font-weight:700; color:#2a3553; }
          .pane iframe, .pane object { flex:1 1 auto; }
          .df-compare.fullscreen { position: fixed; inset: 0; background:#fff; z-index: 1000; padding: 16px 20px; overflow:auto; }
          .df-compare.fullscreen .pane iframe,
          .df-compare.fullscreen .pane object { height: calc(100vh - 160px); }

          .df-demo .video-full { width:100%; height: 65vh; background:#000; border-radius:12px; overflow:hidden; border:1px solid #cfd9ea; }

          @media (max-width: 960px) {
            .flow-grid { grid-template-columns: 1fr; }
            .notes-grid { grid-template-columns: 1fr; }
            .compare-grid { grid-template-columns: 1fr; }
          }
        `}</style>
      </main>
    </div>
  );
}
